import type React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { IconProvider, AiChat, AiChatMessageSender, useSierraOrchestration } from '@shipstation/aiui';
import type {
  AiChatAnnouncement,
  AiChatSuggestion,
  AiChatRenderContext,
  SurveyResponse,
  AiChatAttachment,
  AiChatFileAttachment,
  AiuiLogger,
} from '@shipstation/aiui';
import classnames from 'classnames';

import { FlexColumn } from 'ui-components/components/flex/column';
import { GuidService } from 'ui-components/services/guid-service';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import { useToggle } from 'ui-components/hooks/use-toggle';

import { LogService } from '../../../services/log-service';
import { Segment } from '../../../services/analytics-services/segment';
import { ShippyEvents } from '../../../tracking/tracking-constants';
import { useExampleQuestions } from '../hooks/use-example-questions';
import { useShippyAssistant } from '../hooks/use-shippy-assistant';
import { useSierraHelpers } from '../hooks/use-sierra-helpers';
import { useSierraVariables, formatPriorConversation } from '../hooks/use-sierra-variables';
import { useEnableShippyTriage } from '../../../feature-flags/hooks/use-enable-shippy-triage';
import { MESSAGE_ROLES } from '../constants';
import { ShippyResponseFooter } from '../shippy-response-footer';
import { SIERRA_AGENT_TOKEN } from '../../../global/support-chat/sierra-chat/sierra-chat-service';
import { disclaimerValues, messages } from '../messages';
import { useShippySidebar } from '../shippy-sidebar-context';
import { clearPersistedConversation, loadPersistedConversation, savePersistedConversation } from '../shippy-persistence';
import type { ShippyMessage, ShippyMode } from '../shippy-persistence';
import { aiChatIcons } from './ai-chat-icons';
import style from './style.module.less';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const THUMBS_DOWN_SCORE = 1;

const aiuiLogger: AiuiLogger = {
  debug: noop,
  info: (msg, ctx) => LogService.logInfo(`[aiui] ${msg}`, { ...ctx, source: 'aiui' }),
  warn: (msg, ctx) => LogService.logWarning(`[aiui] ${msg}`, { ...ctx, source: 'aiui' }),
  error: (msg, ctx) => LogService.logError(new Error(`[aiui] ${msg}`, { cause: ctx?.error }), { ...ctx, source: 'aiui' }),
};

type ShippySidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

const DEFAULT_HEADER_HEIGHT = 48;

const SENDER_TO_ROLE: Record<string, string> = {
  [AiChatMessageSender.USER]: MESSAGE_ROLES.USER,
  [AiChatMessageSender.ASSISTANT]: MESSAGE_ROLES.ASSISTANT,
};

const convertToApiHistory = (msgs: ShippyMessage[]) =>
  msgs
    .filter((msg) => msg.sender === AiChatMessageSender.USER || msg.sender === AiChatMessageSender.ASSISTANT)
    .map((msg) => ({ role: SENDER_TO_ROLE[msg.sender], content: msg.content }));

export const ShippySidebar = ({ isOpen, onClose }: ShippySidebarProps) => {
  const intl = useIntl();
  const { setHasUnreadMessages: onUnreadChange } = useShippySidebar();

  const [sidebarTopOffset, setSidebarTopOffset] = useState(DEFAULT_HEADER_HEIGHT);
  const { isActivated: isExpanded, activate: expandSidebar, deactivate: collapseSidebar } = useToggle(false);
  const onToggleExpand = isExpanded ? collapseSidebar : expandSidebar;

  useEffect(() => {
    if (!isOpen) {
      collapseSidebar();
      return;
    }

    const header = document.querySelector('[data-app-header]');
    if (!header) {
      return;
    }

    const headerBottom = header.getBoundingClientRect().bottom;
    setSidebarTopOffset(headerBottom);
  }, [isOpen, collapseSidebar]);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [mode, setMode] = useState<ShippyMode>('shippy');
  const [messagesState, setMessages] = useState<ShippyMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLiveAgentConnected, setIsLiveAgentConnected] = useState(false);
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  const [announcements, setAnnouncements] = useState<AiChatAnnouncement[]>([]);

  // Track turn number for feedback
  const turnNumberRef = useRef(1);

  // Track live agent state
  const humanAgentNameRef = useRef<string | null>(null);
  const hasReceivedFirstAgentMessageRef = useRef(false); // Ref for sync checks in callbacks
  const [hasReceivedFirstAgentMessage, setHasReceivedFirstAgentMessage] = useState(false); // State for rendering
  const isLiveAgentConnectedRef = useRef(false); // Ref for sync checks in inactivity timer
  const isConversationEndedRef = useRef(false); // Ref for sync checks in inactivity timer

  // Inactivity timer - shows check-in message after 5 minutes of inactivity
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityFiredRef = useRef(false);

  // Track if we've initialized (only init on first open, not mount)
  const hasInitializedRef = useRef(false);

  // Debounce timer for localStorage persistence
  const persistDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track latest poll cursor for persistence across reloads
  const pollCursorRef = useRef<string | null>(null);

  // Track sidebar open state and callback in refs for use in polling callbacks
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;

  const { callShippyApi } = useShippyAssistant();
  const [conversationId, setConversationId] = useState(() => {
    const persisted = loadPersistedConversation();
    if (persisted && !persisted.isConversationEnded && persisted.conversationId) {
      return persisted.conversationId;
    }
    return GuidService.newGuid();
  });
  const { createSystemMessage } = useSierraHelpers();
  const { sierraVariables, mappedLocale } = useSierraVariables();
  const isTriageEnabled = useEnableShippyTriage();

  // Sierra orchestration hook
  const {
    startSession,
    clearSession: clearSierraSession,
    sendToSierra,
    sendFiles,
    endConversation,
    state: sierraState,
    startLivePolling,
    stopLivePolling,
    submitSurvey,
    restoreSession,
  } = useSierraOrchestration({
    agentToken: SIERRA_AGENT_TOKEN,
    locale: mappedLocale,
    variables: sierraVariables,
    logger: aiuiLogger,
  });

  const emptyStateSuggestions = useExampleQuestions();

  // Inactivity timer helpers
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    // Don't start if already fired, conversation ended, or in live agent mode
    if (inactivityFiredRef.current || isLiveAgentConnectedRef.current || isConversationEndedRef.current) return;

    inactivityTimerRef.current = setTimeout(() => {
      if (isLiveAgentConnectedRef.current || isConversationEndedRef.current) return;
      inactivityFiredRef.current = true;
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          content: intl.formatMessage(messages.inactivityCheckIn),
          sender: AiChatMessageSender.ASSISTANT,
          timestamp: new Date(),
        },
      ]);
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer, intl]);

  // Clear inactivity timer on unmount
  useEffect(() => {
    return () => {
      clearInactivityTimer();
    };
  }, [clearInactivityTimer]);

  // Sync refs and clear inactivity timer when entering live agent mode or conversation ends
  useEffect(() => {
    isLiveAgentConnectedRef.current = isLiveAgentConnected;
    if (isLiveAgentConnected) {
      clearInactivityTimer();
    }
  }, [isLiveAgentConnected, clearInactivityTimer]);

  useEffect(() => {
    isConversationEndedRef.current = isConversationEnded;
    if (isConversationEnded) {
      clearInactivityTimer();
    }
  }, [isConversationEnded, clearInactivityTimer]);

  const onAnnouncement = useCallback((incoming: AiChatAnnouncement | AiChatAnnouncement[]) => {
    const items = Array.isArray(incoming) ? incoming : [incoming];
    const newAnnouncements = items.filter(Boolean);
    if (newAnnouncements.length > 0) {
      setAnnouncements((prev) => {
        const existingIds = new Set(prev.map((a) => a.id));
        const toAdd = newAnnouncements.filter((a) => !existingIds.has(a.id));
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    }
  }, []);

  const onAttachment = useCallback(
    (attachment: AiChatAttachment, targetMessageId: string | null) => {
      if (targetMessageId) {
        // Attach survey to the target message
        setMessages((prev) => prev.map((msg) => (msg.id === targetMessageId ? { ...msg, attachment } : msg)));
      }
    },
    [setMessages],
  );

  // Initialize Sierra session - restore from localStorage on mount, or start new session on first open.
  // Component is always mounted (parent uses CSS visibility), so hooks run immediately.
  // Restoring a cached session happens on mount so live agent polling and unread notifications work
  // even before the sidebar is opened. New sessions wait for first open to avoid unnecessary API calls.
  useEffect(() => {
    if (hasInitializedRef.current) return;

    const cached = loadPersistedConversation();

    // Restore cached session on mount (no need to wait for isOpen)
    if (sierraState && cached && !cached.isConversationEnded) {
      hasInitializedRef.current = true;

      let restoredMessages = cached.messages;

      // Patch stale 'connecting' dividers if agent already connected before reload
      if (cached.isLiveAgentConnected && cached.hasReceivedFirstAgentMessage) {
        restoredMessages = restoredMessages.map((msg) =>
          msg.sender === AiChatMessageSender.SYSTEM && msg.systemType === 'connecting'
            ? { ...msg, content: intl.formatMessage(messages.agentConnectedMessage), systemType: 'agent-connected' as const }
            : msg,
        );
      }

      setMessages(restoredMessages);
      setMode(cached.mode);
      setIsLiveAgentConnected(cached.isLiveAgentConnected);
      setIsConversationEnded(cached.isConversationEnded);
      setAnnouncements(cached.announcements ?? []);
      turnNumberRef.current = cached.turnNumber;
      inactivityFiredRef.current = cached.inactivityFired ?? false;

      // Restore live agent state: recover agent name from messages, restore cursor, resume polling
      if (cached.isLiveAgentConnected) {
        const hadFirstMessage = cached.hasReceivedFirstAgentMessage ?? false;
        hasReceivedFirstAgentMessageRef.current = hadFirstMessage;
        setHasReceivedFirstAgentMessage(hadFirstMessage);

        const lastAgentMsg = [...cached.messages].reverse().find((msg) => msg.sender === AiChatMessageSender.LIVE_AGENT);
        humanAgentNameRef.current = lastAgentMsg?.agentName ?? null;

        if (cached.pollCursor) {
          pollCursorRef.current = cached.pollCursor;
          restoreSession(sierraState, cached.pollCursor);
        }

        startLiveAgentPolling();
      }

      return;
    }

    // New session - wait for first open to avoid unnecessary API calls
    if (!isOpen) return;

    hasInitializedRef.current = true;
    clearPersistedConversation();
    startSession({ onAnnouncement });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- init on mount (restore) or first open (new session)
  }, [isOpen, sierraState]);

  // Debounced persist - avoids saving on every streaming token
  useEffect(() => {
    if (messagesState.length === 0) return;

    // Clear any pending save
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
    }

    // Save after 500ms of no changes
    persistDebounceRef.current = setTimeout(() => {
      savePersistedConversation({
        conversationId,
        messages: messagesState,
        mode,
        isLiveAgentConnected,
        isConversationEnded,
        hasReceivedFirstAgentMessage,
        turnNumber: turnNumberRef.current,
        announcements,

        inactivityFired: inactivityFiredRef.current,
        pollCursor: pollCursorRef.current,
        lastUpdated: Date.now(),
      });
    }, 500);

    return () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
      }
    };
  }, [messagesState, mode, isLiveAgentConnected, isConversationEnded, hasReceivedFirstAgentMessage, announcements, conversationId]);

  // Refocus input when it becomes re-enabled (after response received)
  const isInputDisabled = isConversationEnded || (!isLiveAgentConnected && isLoading);
  const prevInputDisabledRef = useRef(isInputDisabled);
  useEffect(() => {
    const wasDisabled = prevInputDisabledRef.current;
    prevInputDisabledRef.current = isInputDisabled;

    // Focus when transitioning from disabled to enabled
    if (wasDisabled && !isInputDisabled) {
      inputRef.current?.focus();
    }
  }, [isInputDisabled]);

  // Clear unread and focus input when sidebar opens
  useEffect(() => {
    if (isOpen) {
      onUnreadChange?.(false);
      if (!isInputDisabled) {
        inputRef.current?.focus();
      }
    }
  }, [isOpen, isInputDisabled, onUnreadChange]);

  // Handle user manually ending conversation - notify Sierra API, show goodbye/survey, disable input
  const handleEndConversation = useCallback(() => {
    if (isConversationEnded) return;

    Segment.trackEvent(ShippyEvents.SIERRA_CONVERSATION_ENDED, {
      conversationId,
      totalTurns: turnNumberRef.current,
      hadLiveAgent: isLiveAgentConnectedRef.current,
      endReason: 'user-ended',
    });

    // Stop live agent polling
    stopLivePolling();
    setIsLiveAgentConnected(false);
    setIsConversationEnded(true);

    // Track streaming message for goodbye/survey response, as they are "attachments" to a message
    let endConvoMsgId: string | null = null;
    const endConvoTurnNumber = turnNumberRef.current;

    // Notify Sierra API regardless of mode since we initialize a conversation with Sierra at start
    setIsLoading(true);
    endConversation({
      onToken: (token) => {
        if (!endConvoMsgId) {
          const msgId = crypto.randomUUID();
          endConvoMsgId = msgId;
          setMessages((prev) => [
            ...prev,
            {
              id: msgId,
              content: token,
              sender: AiChatMessageSender.ASSISTANT,
              timestamp: new Date(),
              turnNumber: endConvoTurnNumber,
            },
          ]);
          setIsLoading(false);
        } else {
          setMessages((prev) => prev.map((msg) => (msg.id === endConvoMsgId ? { ...msg, content: msg.content + token } : msg)));
        }
      },
      onEndConversation: () => {
        setIsLoading(false);
        setMessages((prev) => [...prev, createSystemMessage('sessionEnded')]);
      },
      onEndOfMessage: () => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
      onAttachment: (attachment) => onAttachment(attachment, endConvoMsgId),
    });
  }, [isConversationEnded, endConversation, stopLivePolling, onAttachment, createSystemMessage, conversationId]);

  /**
   * Remove the survey message entirely so the survey UI and its parent message
   * (e.g. "Is your issue fully resolved?") both disappear after submission.
   */
  const removeSurveyMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  /**
   * Add a "Session ended" divider if not already present, and mark conversation as ended.
   */
  const addSessionEndedDivider = useCallback(() => {
    setMessages((prev) => {
      const hasSessionEnded = prev.some((msg) => msg.sender === AiChatMessageSender.SYSTEM && msg.systemType === 'session-ended');
      if (hasSessionEnded) return prev;
      return [...prev, createSystemMessage('sessionEnded')];
    });
    setIsConversationEnded(true);
  }, [createSystemMessage]);

  /**
   * Build streaming callbacks for submitSurvey.
   * For thumbs-down, if Sierra streams a follow-up message we suppress onEndConversation
   * and re-enable input. If Sierra sends no follow-up (just ends), we finalize normally.
   */
  const createSurveyCallbacks = useCallback(
    (isNegative: boolean) => {
      let streamingMsgId: string | null = null;
      let hasReceivedTokens = false;

      return {
        onToken: (token: string) => {
          hasReceivedTokens = true;
          if (!streamingMsgId) {
            const msgId = crypto.randomUUID();
            streamingMsgId = msgId;
            setMessages((prev) => [
              ...prev,
              {
                id: msgId,
                content: token,
                sender: AiChatMessageSender.ASSISTANT,
                timestamp: new Date(),
              },
            ]);
          } else {
            setMessages((prev) => prev.map((msg) => (msg.id === streamingMsgId ? { ...msg, content: msg.content + token } : msg)));
          }
        },
        onEndOfMessage: () => {
          streamingMsgId = null;
          if (isNegative && hasReceivedTokens) {
            // Sierra sent a follow-up — re-enable input so user can continue chatting
            setIsConversationEnded(false);
          }
        },
        onEndConversation: () => {
          if (isNegative && hasReceivedTokens) {
            // Sierra sent a follow-up — suppress the end so conversation stays open
            return;
          }
          addSessionEndedDivider();
        },
        onError: () => {
          streamingMsgId = null;
          addSessionEndedDivider();
        },
      };
    },
    [addSessionEndedDivider],
  );

  /**
   * Handle survey submission - sends response to Sierra.
   * For negative (thumbs-down) feedback, Sierra streams a follow-up message
   * and we suppress onEndConversation so the conversation doesn't immediately close.
   */
  const handleSurveySubmit = useCallback(
    async (messageId: string, response: SurveyResponse) => {
      removeSurveyMessage(messageId);

      Segment.trackEvent(ShippyEvents.SURVEY_RESPONSE, {
        conversationId,
        turnNumber: turnNumberRef.current,
        surveySkipped: false,
        mode,
        surveyResponse: response,
      });

      const isNegative = response.type === 'thumbs-up-down-survey-response' && response.score === THUMBS_DOWN_SCORE;
      try {
        await submitSurvey({ ...response } as Record<string, unknown>, createSurveyCallbacks(isNegative));
      } catch {
        addSessionEndedDivider();
      }
    },
    [removeSurveyMessage, submitSurvey, createSurveyCallbacks, addSessionEndedDivider, conversationId, mode],
  );

  /**
   * Handle survey skip - sends skip to Sierra.
   */
  const handleSurveySkip = useCallback(
    async (messageId: string) => {
      removeSurveyMessage(messageId);

      Segment.trackEvent(ShippyEvents.SURVEY_RESPONSE, {
        conversationId,
        turnNumber: turnNumberRef.current,
        surveyScore: null,
        surveySkipped: true,
        mode,
      });

      try {
        await submitSurvey({ type: 'skip-feedback' }, createSurveyCallbacks(false));
      } catch {
        addSessionEndedDivider();
      }
    },
    [removeSurveyMessage, submitSurvey, createSurveyCallbacks, addSessionEndedDivider, conversationId, mode],
  );

  // Start live agent polling - extracted so it can be reused on restore after reload.
  const startLiveAgentPolling = useCallback(() => {
    let liveAgentMsgId: string | null = null;
    let accumulatedAgentMessage = '';

    startLivePolling({
      onToken: (token) => {
        accumulatedAgentMessage += token;
        // Notify unread when new agent messages arrive while sidebar is closed
        if (!isOpenRef.current) {
          onUnreadChangeRef.current?.(true);
        }
        if (!hasReceivedFirstAgentMessageRef.current) {
          hasReceivedFirstAgentMessageRef.current = true;
          setHasReceivedFirstAgentMessage(true);
          // Update 'connecting' divider to 'agent-connected'
          setMessages((prev) =>
            prev.map((msg) =>
              msg.sender === AiChatMessageSender.SYSTEM && msg.systemType === 'connecting'
                ? { ...msg, content: intl.formatMessage(messages.agentConnectedMessage), systemType: 'agent-connected' as const }
                : msg,
            ),
          );
        }
        if (!liveAgentMsgId) {
          const msgId = crypto.randomUUID();
          liveAgentMsgId = msgId;
          setMessages((prev) => [
            ...prev,
            {
              id: msgId,
              content: token,
              sender: AiChatMessageSender.LIVE_AGENT,
              agentName: humanAgentNameRef.current ?? undefined,
              timestamp: new Date(),
            },
          ]);
        } else {
          setMessages((prev) => prev.map((msg) => (msg.id === liveAgentMsgId ? { ...msg, content: msg.content + token } : msg)));
        }
      },
      onEndOfMessage: () => {
        Segment.trackEvent(ShippyEvents.SIERRA_LIVE_AGENT_MESSAGE, {
          conversationId,
          turnNumber: turnNumberRef.current,
          agentName: humanAgentNameRef.current,
          agentMessage: accumulatedAgentMessage,
        });
        liveAgentMsgId = null;
        accumulatedAgentMessage = '';
        setIsLoading(false);
      },
      onError: (liveAgentErrorMessage) => {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            content: liveAgentErrorMessage,
            sender: AiChatMessageSender.ASSISTANT,
            timestamp: new Date(),
          },
        ]);
      },
      onEndConversation: () => {
        handleEndConversation();
      },
      onHumanAgentInfo: (info) => {
        if (info.displayName) {
          humanAgentNameRef.current = info.displayName;
          Segment.trackEvent(ShippyEvents.SIERRA_LIVE_AGENT_CONNECTED, {
            conversationId,
            turnNumber: turnNumberRef.current,
            agentName: info.displayName,
          });
          if (!hasReceivedFirstAgentMessageRef.current) {
            hasReceivedFirstAgentMessageRef.current = true;
            setHasReceivedFirstAgentMessage(true);
            // Update 'connecting' divider to 'agent-connected'
            setMessages((prev) =>
              prev.map((msg) =>
                msg.sender === AiChatMessageSender.SYSTEM && msg.systemType === 'connecting'
                  ? { ...msg, content: intl.formatMessage(messages.agentConnectedMessage), systemType: 'agent-connected' as const }
                  : msg,
              ),
            );
          }
        }
      },
      onLivePollCursor: (cursor) => {
        pollCursorRef.current = cursor;
      },
    });
  }, [startLivePolling, intl, handleEndConversation, conversationId]);

  /**
   * Create Sierra streaming callbacks for a given turn.
   * Used by both direct Sierra mode and Shippy→Sierra handoff.
   *
   * Returns `{ callbacks, finalize }`. Call `finalize()` after the stream
   * completes (i.e. after `await sendToSierra` resolves) to handle deferred
   * endConversation events. Sierra sometimes sends endConversation BEFORE
   * a follow-up goodbye message, so we defer the session-ended UI until
   * onEndOfMessage fires. If no tokens follow, finalize() handles cleanup.
   */
  const createSierraCallbacks = useCallback(
    (turnNumber: number) => {
      // Scoped variable for streaming message ID - each call gets its own
      let streamingMsgId: string | null = null;
      let conversationEnding = false;
      let accumulatedResponse = '';

      return {
        callbacks: {
          onToken: (token: string) => {
            accumulatedResponse += token;
            if (!streamingMsgId) {
              // Notify unread when new messages arrive while sidebar is closed
              if (!isOpenRef.current) {
                onUnreadChangeRef.current?.(true);
              }
              // Create message on first token
              const msgId = crypto.randomUUID();
              streamingMsgId = msgId;
              setMessages((prev) => [
                ...prev,
                {
                  id: msgId,
                  content: token,
                  sender: AiChatMessageSender.ASSISTANT,
                  timestamp: new Date(),
                  turnNumber,
                },
              ]);
              setIsLoading(false);
            } else {
              // Append to existing message
              setMessages((prev) => prev.map((msg) => (msg.id === streamingMsgId ? { ...msg, content: msg.content + token } : msg)));
            }
          },
          onEndOfMessage: () => {
            Segment.trackEvent(ShippyEvents.SIERRA_RESPONSE_RECEIVED, {
              conversationId,
              turnNumber,
              sierraResponse: accumulatedResponse,
            });
            setIsLoading(false);
            streamingMsgId = null;
            accumulatedResponse = '';
            if (conversationEnding) {
              conversationEnding = false;
              addSessionEndedDivider();
            } else {
              startInactivityTimer();
            }
          },
          onError: (errorMessage: string) => {
            if (!streamingMsgId) {
              // Create error message if no message exists yet
              const msgId = crypto.randomUUID();
              setMessages((prev) => [
                ...prev,
                {
                  id: msgId,
                  content: errorMessage,
                  sender: AiChatMessageSender.ASSISTANT,
                  timestamp: new Date(),
                  turnNumber,
                },
              ]);
            } else {
              setMessages((prev) => prev.map((msg) => (msg.id === streamingMsgId ? { ...msg, content: errorMessage } : msg)));
            }
            setIsLoading(false);
            streamingMsgId = null;
          },
          onTransfer: () => {
            // Reset live agent tracking state
            hasReceivedFirstAgentMessageRef.current = false;
            setHasReceivedFirstAgentMessage(false);
            humanAgentNameRef.current = null;

            // Add transfer message
            setMessages((prev) => [...prev, createSystemMessage('transfer')]);
            setIsLiveAgentConnected(true);
            setIsLoading(false);
            streamingMsgId = null;

            startLiveAgentPolling();
          },
          onAttachment: (attachment: AiChatAttachment) => onAttachment(attachment, streamingMsgId),
          onEndConversation: () => {
            Segment.trackEvent(ShippyEvents.SIERRA_CONVERSATION_ENDED, {
              conversationId,
              totalTurns: turnNumberRef.current,
              hadLiveAgent: isLiveAgentConnectedRef.current,
              endReason: 'session-expired',
            });
            // Defer handling — Sierra may stream a goodbye message after this event.
            // finalize() or onEndOfMessage will handle the session-ended UI.
            conversationEnding = true;
            clearInactivityTimer();
            setIsLoading(false);
            streamingMsgId = null;
          },
        },
        /** Call after the stream completes to handle endConversation with no follow-up tokens. */
        finalize: () => {
          if (conversationEnding) {
            conversationEnding = false;
            // endConversation fired but no tokens/onEndOfMessage followed — end session now
            addSessionEndedDivider();
          }
        },
      };
    },
    [
      addSessionEndedDivider,
      clearInactivityTimer,
      conversationId,
      createSystemMessage,
      onAttachment,
      startInactivityTimer,
      startLiveAgentPolling,
    ],
  );

  /**
   * Handle sending messages - routes to Shippy or Sierra based on mode.
   */
  const handleSend = useCallback(
    async (text: string) => {
      // Reset inactivity timer when user sends a message
      clearInactivityTimer();
      inactivityFiredRef.current = false;

      // Add user message to state
      const userMsg: ShippyMessage = {
        id: crypto.randomUUID(),
        content: text,
        sender: AiChatMessageSender.USER,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);

      const currentTurnNumber = turnNumberRef.current;
      turnNumberRef.current += 1;

      // Live agent — bypass Shippy, talk directly to Sierra (no loading — polling is async)
      if (isLiveAgentConnected) {
        const { callbacks: sierraCallbacks, finalize } = createSierraCallbacks(currentTurnNumber);
        try {
          await sendToSierra(text, sierraCallbacks);
        } finally {
          finalize();
        }
        return;
      }

      // Triage off — Sierra mode messages go directly to Sierra (legacy behavior)
      if (mode === 'sierra' && !isTriageEnabled) {
        setIsLoading(true);
        const { callbacks: sierraCallbacks, finalize } = createSierraCallbacks(currentTurnNumber);
        try {
          await sendToSierra(text, sierraCallbacks);
        } finally {
          finalize();
        }
        return;
      }

      // Route through Shippy first — Shippy decides whether to handle or defer to Sierra
      setIsLoading(true);
      try {
        const apiHistory = convertToApiHistory(messagesState);
        const response = await callShippyApi(text, apiHistory, currentTurnNumber, conversationId);

        // Shippy determines this is a shippy responsibility
        if (response.capability?.id === 'sierra-headless') {
          setMode('sierra');

          Segment.trackEvent(ShippyEvents.SIERRA_ESCALATION, {
            conversationId,
            turnNumber: currentTurnNumber,
            summary: response.capability.parameters?.summary,
          });

          const handoffVariables = { ...sierraVariables, PRIOR_CONVERSATION: formatPriorConversation(messagesState) };
          const { callbacks: handoffCallbacks, finalize } = createSierraCallbacks(currentTurnNumber);
          try {
            await sendToSierra(text, handoffCallbacks, { variables: handoffVariables, updateVariables: true });
          } finally {
            finalize();
          }
          return;
        }

        // Shippy handles it — render the response
        setMode('shippy');

        let suggestions: AiChatSuggestion[] | undefined;
        if (response.clarification?.options) {
          suggestions = response.clarification.options.map((option, index) => {
            const label = typeof option === 'string' ? option : (option.label ?? option.value ?? '');
            return { id: `clarification-${index}`, label };
          });
        }

        const assistantMsg: ShippyMessage = {
          id: crypto.randomUUID(),
          content: response.message,
          sender: AiChatMessageSender.ASSISTANT,
          timestamp: new Date(),
          suggestions,
          turnNumber: currentTurnNumber,
          capability: response.capability,
          commandId: response.command?.commandId,
          parameters: response.command?.parameters as Record<string, string> | undefined,
        };
        setMessages((prev) => [...prev, assistantMsg]);
        startInactivityTimer();
      } catch (error) {
        LogService.logError(new Error('Shippy chat API request failed', { cause: error }));
        const errorMsg: ShippyMessage = {
          id: crypto.randomUUID(),
          content: intl.formatMessage(messages.errorMessage),
          sender: AiChatMessageSender.ASSISTANT,
          timestamp: new Date(),
          turnNumber: currentTurnNumber,
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      callShippyApi,
      clearInactivityTimer,
      conversationId,
      intl,
      isLiveAgentConnected,
      isTriageEnabled,
      messagesState,
      mode,
      sendToSierra,
      createSierraCallbacks,
      sierraVariables,
      startInactivityTimer,
    ],
  );

  // Handle file upload - uploads to Sierra, adds user message with file previews.
  // Add a placeholder user message first, then update it with file metadata after upload.
  // This ensures the user message appears before any bot response triggered by the upload.
  const handleFileSelect = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const msgId = crypto.randomUUID();

      // Add placeholder user message immediately so it appears before the bot response
      const placeholderFiles = files.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));
      setMessages((prev) => [
        ...prev,
        {
          id: msgId,
          content: '',
          sender: AiChatMessageSender.USER,
          timestamp: new Date(),
          files: placeholderFiles,
        },
      ]);

      const { callbacks: fileCallbacks, finalize: fileFinalize } = createSierraCallbacks(turnNumberRef.current);
      let attachments: AiChatFileAttachment[] = [];
      try {
        attachments = await sendFiles(files, fileCallbacks);
      } finally {
        fileFinalize();
      }

      // Update placeholder with actual attachment data (e.g. preview URLs)
      if (attachments.length > 0) {
        setMessages((prev) => prev.map((msg) => (msg.id === msgId ? { ...msg, files: attachments } : msg)));
      }
    },
    [sendFiles, createSierraCallbacks],
  );

  // Handle suggestion click
  const handleSelectSuggestion = useCallback(
    (suggestion: AiChatSuggestion) => {
      if (turnNumberRef.current === 1) {
        Segment.trackEvent(ShippyEvents.EXAMPLE_QUESTION_CLICKED, {
          question: suggestion.label,
          conversationId,
        });
      }
      // Clear suggestions from the message that had them
      setMessages((prev) => prev.map((msg) => (msg.suggestions ? { ...msg, suggestions: undefined } : msg)));
      handleSend(suggestion.label);
    },
    [handleSend, conversationId],
  );

  const resetConversation = useCallback(() => {
    clearSierraSession();
    clearInactivityTimer();
    clearPersistedConversation();

    setMode('shippy');
    setMessages([]);
    setAnnouncements([]);
    setIsLoading(false);
    setIsLiveAgentConnected(false);
    setIsConversationEnded(false);
    setHasReceivedFirstAgentMessage(false);
    turnNumberRef.current = 1;
    humanAgentNameRef.current = null;
    hasReceivedFirstAgentMessageRef.current = false;
    inactivityFiredRef.current = false;
    pollCursorRef.current = null;
    setConversationId(GuidService.newGuid());
  }, [clearInactivityTimer, clearSierraSession]);

  const handleOnClose = useCallback(() => {
    // Just hide the sidebar - don't reset conversation (state persists across open/close)
    onClose();
  }, [onClose]);

  const handleStartNewChat = useCallback(() => {
    resetConversation();
    startSession({ onAnnouncement });
  }, [onAnnouncement, resetConversation, startSession]);

  /**
   * Render footer for assistant messages.
   * SSUI owns the messages, so we access metadata directly - no correlation needed.
   */
  const renderMessageFooter = useCallback(
    (context: AiChatRenderContext) => {
      const { message, index } = context;

      // Only render footer for assistant messages
      if (message.sender !== AiChatMessageSender.ASSISTANT) {
        return null;
      }

      // Get the message from our owned state (typed as ShippyMessage)
      const shippyMessage = messagesState[index] as ShippyMessage | undefined;
      if (!shippyMessage) {
        return null;
      }

      // Skip footer for messages with attachments (surveys)
      if (message.attachment) {
        return null;
      }

      // All context is captured directly on the message - no searching needed
      return (
        <ShippyResponseFooter
          capability={shippyMessage.capability}
          conversationId={conversationId}
          isRehydrated={shippyMessage.isRehydrated ?? false}
          turnNumber={shippyMessage.turnNumber ?? 0}
        />
      );
    },
    [messagesState, conversationId],
  );

  return (
    <FlexColumn
      aria-modal='true'
      className={classnames(style.sidebar, { [style.open]: isOpen, [style.expanded]: isExpanded })}
      role='dialog'
      style={{ '--sidebar-top-offset': `${sidebarTopOffset}px` } as React.CSSProperties}
    >
      <div className={style.sierraChatContainer}>
        <IconProvider icons={aiChatIcons}>
          <AiChat
            acceptedFileTypes='image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.zip,.tar,.xml,.json'
            announcements={announcements}
            data-ignore-root-focus
            hideAnnouncementDismiss
            hideHistoryButton
            hideNewChatButton
            hideUploadButton={mode === 'shippy'}
            inputPlaceholder={intl.formatMessage(messages.promptPlaceholder)}
            inputRef={inputRef}
            isExpanded={isExpanded}
            isInputDisabled={isInputDisabled}
            isLiveAgentConnected={isLiveAgentConnected}
            isLoading={isLoading}
            messages={messagesState}
            onClose={handleOnClose}
            onEndConversation={handleEndConversation}
            onFileSelect={handleFileSelect}
            onSelectSuggestion={handleSelectSuggestion}
            onSendMessage={handleSend}
            onStartNewChat={handleStartNewChat}
            onSurveySkip={handleSurveySkip}
            onSurveySubmit={handleSurveySubmit}
            onToggleExpand={onToggleExpand}
            renderMessageFooter={renderMessageFooter}
            suggestions={messagesState.length === 0 ? emptyStateSuggestions : undefined}
            welcomeMessage={intl.formatMessage(messages.emptyStateHeading)}
          />
        </IconProvider>
      </div>
      <Text
        className={style.sierraDisclaimer}
        formattedMessageValues={disclaimerValues}
        textType={TextType.Paragraph}
        value={messages.dataProcessingDisclaimer}
      />
    </FlexColumn>
  );
};
